export type MarketingSocialFormat = 'story_slides' | 'carousel' | 'quote_card';
export type MarketingCampaignStatus = 'planning' | 'active' | 'completed';
export type MarketingCampaignChannel = 'tiktok' | 'instagram' | 'facebook' | 'youtube' | 'mixed';
export type MarketingCampaignAssetKind = 'video' | 'character' | 'social';

export interface MarketingSocialCard {
  id: string;
  text: string;
  role: 'hook' | 'story' | 'quote' | 'mood' | 'cta';
  sourceItemId?: string;
  exactQuote?: boolean;
  exactSource?: boolean;
}

export interface MarketingSocialDraft {
  format: MarketingSocialFormat;
  editionLanguage: string;
  title: string;
  selectedSourceIds: string[];
  tone: string;
  callToAction: string;
  spoilerLevel: 'low' | 'medium' | 'high';
  campaignNote: string;
  caption: string;
  cards: MarketingSocialCard[];
  updatedAt: string;
}

export interface MarketingSocialAsset extends MarketingSocialDraft {
  id: string;
  createdAt: string;
}

export interface MarketingCampaignAssetRef {
  kind: MarketingCampaignAssetKind;
  id: string;
}

export interface MarketingCampaign {
  id: string;
  name: string;
  objective: string;
  channel: MarketingCampaignChannel;
  status: MarketingCampaignStatus;
  notes: string;
  assetRefs: MarketingCampaignAssetRef[];
  createdAt: string;
  updatedAt: string;
}

export interface MarketingWorkspaceState {
  version: 1;
  socialDrafts: Record<string, MarketingSocialDraft>;
  socialAssets: MarketingSocialAsset[];
  campaigns: MarketingCampaign[];
}

const EMPTY_STATE: MarketingWorkspaceState = {
  version: 1,
  socialDrafts: {},
  socialAssets: [],
  campaigns: [],
};

const isObject = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

export const marketingWorkspaceStorageKey = (userId: string, bookId: string): string => (
  `scribeshadow.marketing.workspace.v1:${encodeURIComponent(userId)}:${encodeURIComponent(bookId)}`
);

export const socialDraftKey = (editionLanguage: string, format: MarketingSocialFormat): string => (
  `${editionLanguage.trim().toLowerCase() || 'original'}:${format}`
);

export const parseMarketingWorkspaceState = (raw: string | null): MarketingWorkspaceState => {
  if (!raw) return { ...EMPTY_STATE };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isObject(parsed)) return { ...EMPTY_STATE };
    return {
      version: 1,
      socialDrafts: isObject(parsed.socialDrafts)
        ? parsed.socialDrafts as Record<string, MarketingSocialDraft>
        : {},
      socialAssets: Array.isArray(parsed.socialAssets)
        ? parsed.socialAssets.filter(isObject) as unknown as MarketingSocialAsset[]
        : [],
      campaigns: Array.isArray(parsed.campaigns)
        ? parsed.campaigns.filter(isObject) as unknown as MarketingCampaign[]
        : [],
    };
  } catch {
    return { ...EMPTY_STATE };
  }
};

export const loadMarketingWorkspaceState = (userId: string, bookId: string): MarketingWorkspaceState => {
  if (typeof window === 'undefined') return { ...EMPTY_STATE };
  try {
    return parseMarketingWorkspaceState(window.localStorage.getItem(marketingWorkspaceStorageKey(userId, bookId)));
  } catch {
    return { ...EMPTY_STATE };
  }
};

export const saveMarketingWorkspaceState = (
  userId: string,
  bookId: string,
  state: MarketingWorkspaceState
): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(marketingWorkspaceStorageKey(userId, bookId), JSON.stringify({
      ...state,
      version: 1,
    }));
  } catch {
    // Private browsing and storage quotas can disable persistence. The in-memory
    // workspace still works for the current session.
  }
};

export const createMarketingId = (prefix: string): string => {
  const randomPart = Math.random().toString(36).slice(2, 9);
  return `${prefix}-${Date.now().toString(36)}-${randomPart}`;
};
